/**
 * UNIFIED MODAL STYLE — the shared look for the app's modals.
 *
 * Every modal here was styled independently, and they drifted: three different
 * corner radii, primary buttons between 44px and 56px tall in two different
 * blues, section labels that were 10px uppercase letter-spaced in one place and
 * ordinary text in another, and the same PIN input implemented twice at
 * different sizes. Naming the pattern is only worth anything if it lives
 * somewhere a new modal can import it, so it lives here.
 *
 * The values come from YapTabGuidelinesModal, which set the reference look.
 *
 * ── Everything is inline styles, and that is not a preference ──────────────
 * src/index.css is COMMITTED PRE-COMPILED Tailwind. A utility that was not
 * emitted into that bundle does not exist at runtime, however standard it looks
 * in the markup, and it fails silently — no warning, no error, just no styling.
 * That is not hypothetical: `z-99999` on the PIN modal compiled to nothing, so
 * it had no stacking context and the guide overlay rendered on top of it, and
 * `animate-spin` had no rule at all, so every spinner in the app was a
 * motionless circle. Arbitrary values (`bg-[#1D9BF0]`), arbitrary z-indexes and
 * `focus:` variants on new colours are all in that category. Use these tokens.
 *
 * Usage:
 *   import { MODAL, modalPrimaryButton } from './ui/unifiedModalStyle';
 *   <div style={MODAL.content}> … </div>
 *   <button style={modalPrimaryButton(canSubmit)}>Continue</button>
 */

import type { CSSProperties } from 'react';

/** Brand blue. The one accent for focus rings and primary actions. */
export const MODAL_ACCENT = '#1D9BF0';

export const MODAL = {
  /**
   * The modal box itself.
   *
   * maxHeight is dvh rather than vh so the modal shrinks with the dynamic
   * viewport when the keyboard opens, instead of keeping its full height behind
   * the keyboard and pushing the submit button out of reach. It also sizes to
   * its content: a fixed tall modal is where large empty gaps come from.
   */
  shell: {
    maxHeight: 'min(90dvh, 700px)',
    width: '420px',
    maxWidth: 'calc(100vw - 32px)',
    display: 'flex',
    flexDirection: 'column',
  } as CSSProperties,

  /** Icon + title + close. Sticky, so it survives a scrolling body. */
  header: {
    padding: '20px 24px 16px',
    background: 'rgba(13,13,20,0.95)',
    backdropFilter: 'blur(10px)',
  } as CSSProperties,

  /**
   * The scrolling body.
   *
   * `flex: 1 1 auto` with `minHeight: 0` is what makes overflowY actually
   * scroll. Without the flex pair the box grows past the shell's maxHeight and
   * tall content simply runs off the bottom with nothing to scroll; with
   * `flex: 1` alone (no auto basis) it stretches to full height whatever it
   * holds, which is the other failure. This combination gives both: natural
   * height when the content is short, scrolling when it is tall.
   */
  content: {
    padding: '4px 24px 24px',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
  } as CSSProperties,

  /** The one heading per step. */
  heading: {
    fontSize: 20,
    fontWeight: 700,
    color: '#ffffff',
    lineHeight: 1.25,
    textAlign: 'center',
    margin: 0,
  } as CSSProperties,

  /** Helper text under the heading. Normal body size, never 10px uppercase. */
  body: {
    fontSize: 14,
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.55)',
    textAlign: 'center',
    margin: '10px 0 0',
    maxWidth: 300,
  } as CSSProperties,

  /** A label above a field or group. */
  label: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.75)',
  } as CSSProperties,

  /** Text input. */
  input: {
    height: 48,
    borderRadius: 14,
    paddingLeft: 14,
    paddingRight: 14,
    backgroundColor: 'rgba(0,0,0,0.30)',
    border: '1px solid rgba(255,255,255,0.10)',
    color: '#ffffff',
    fontSize: 15,
    textAlign: 'left',
  } as CSSProperties,

  /** The quiet action under the primary button (Skip, Cancel, Not now). */
  secondaryLink: {
    marginTop: 14,
    background: 'none',
    border: 'none',
    padding: '8px 4px',
    fontSize: 14,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.45)',
    cursor: 'pointer',
  } as CSSProperties,

  /** An advisory notice. */
  notice: {
    width: '100%',
    padding: 14,
    borderRadius: 16,
    backgroundColor: 'rgba(245,158,11,0.10)',
    border: '1px solid rgba(245,158,11,0.20)',
  } as CSSProperties,

  /** An error block. */
  error: {
    width: '100%',
    padding: 12,
    borderRadius: 14,
    backgroundColor: 'rgba(239,68,68,0.10)',
    color: '#f87171',
    fontSize: 13,
    lineHeight: 1.45,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  } as CSSProperties,
} as const;

/**
 * The primary action. Full width of the content area.
 *
 * Disabled is a dimmed version of the SAME button rather than a different
 * colour, so it reads as "not yet" instead of "broken".
 */
export function modalPrimaryButton(enabled: boolean): CSSProperties {
  return {
    marginTop: 22,
    width: '100%',
    height: 56,
    borderRadius: 20,
    border: 'none',
    fontSize: 18,
    fontWeight: 700,
    color: '#ffffff',
    backgroundColor: enabled ? MODAL_ACCENT : 'rgba(29,155,240,0.35)',
    boxShadow: enabled ? '0 10px 25px -5px rgba(29,155,240,0.4)' : 'none',
    cursor: enabled ? 'pointer' : 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
  };
}

/**
 * One box of a PIN entry field.
 *
 * Focus is passed in rather than done with a `focus:` variant, because an
 * arbitrary focus colour is exactly the kind of class that is absent from the
 * pre-compiled bundle. Callers track the focused index in state.
 */
export function modalPinBox(filled: boolean, focused: boolean): CSSProperties {
  return {
    width: 60,
    height: 68,
    fontSize: 28,
    fontWeight: 700,
    color: '#ffffff',
    borderRadius: 16,
    transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
    backgroundColor: filled ? 'rgba(29,155,240,0.14)' : 'rgba(255,255,255,0.06)',
    border: `2px solid ${
      focused ? MODAL_ACCENT
        : filled ? 'rgba(29,155,240,0.55)'
        : 'rgba(255,255,255,0.12)'
    }`,
    boxShadow: focused ? `0 0 0 3px ${MODAL_ACCENT}33` : 'none',
    caretColor: 'transparent',
  };
}

/** The spinner shown inside a primary button while it is working. */
export const MODAL_SPINNER: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid rgba(255,255,255,0.35)',
  borderTopColor: '#fff',
  display: 'inline-block',
};
